import React from 'react';

const AboutPage: React.FC = () => {
  console.log('AboutPage rendered - focused on company mission and student benefits');

  return (
      <div className="bg-white text-gray-800">
        {/* Section 1: Mission/Vision */}
        <section className="py-16 sm:py-24 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl md:text-5xl mb-4">
              Our Mission
            </h1>
            <p className="mt-4 max-w-3xl mx-auto text-lg sm:text-xl text-gray-700">
              At LessonLynx, we believe music education works best when it's personal, seamless, and built on trust. Our mission is to empower passionate teachers and curious students by making it easier to connect, communicate, and grow together.
            </p>
            <p className="mt-4 max-w-3xl mx-auto text-lg sm:text-xl text-gray-700">
              We're here to become the most teacher-forward, student-centered lesson platform in the world — by focusing on what matters and delivering tools that actually help.
            </p>
          </div>
        </section>

        {/* Section 2: Student Benefits */}
        <section className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Why Students Choose LessonLynx
              </h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                We're not just a list of names. We're your shortcut to finding the right teacher.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
              <div className="bg-gray-50 p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-blue-700 mb-2">Smart Matching</h3>
                <p className="text-gray-700">Get connected with teachers who actually align with your instrument, goals, and schedule (online or in-person).</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-blue-700 mb-2">All-in-One Simplicity</h3>
                <p className="text-gray-700">Discover, message, and book your lessons in one place — no extra apps or awkward coordination.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-blue-700 mb-2">Trial-Friendly Options</h3>
                <p className="text-gray-700">Not sure who's right for you? Try a discounted trial bundle and explore until it clicks.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-blue-700 mb-2">Reliable & Transparent</h3>
                <p className="text-gray-700">Your teacher is paid upfront. You get consistency, clarity, and commitment from day one.</p>
              </div>
            </div>
          
          {/* Call to Action for Student Pricing */}
          <div className="mt-12 text-center">
            <a 
              href="/pricing" 
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              View Student Pricing
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
          </div>
        </section>

        {/* Section 3: Teacher Benefits */}
        <section className="py-16 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Why Teachers Love Us
              </h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                You became a music teacher to share your craft — not chase payments or juggle tech tools. LessonLynx handles the overhead so you can focus on the music.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Free to Join</h3>
                    <p className="text-gray-700">No listing fees, no subscriptions — just open the door to new students.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Centralized Booking & Messaging</h3>
                    <p className="text-gray-700">Keep it all in one clean, easy-to-use dashboard.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Guaranteed Payouts</h3>
                    <p className="text-gray-700">We manage billing and cancellations so you're paid promptly and fairly.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Scales With You</h3>
                    <p className="text-gray-700">As your student count grows, our commission goes down — because success should be rewarded.</p>
                </div>
            </div>
          
          {/* Call to Action for Teachers */}
          <div className="mt-12 text-center">
            <a 
              href="/for-teachers" 
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-yellow-600 hover:bg-yellow-700 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Learn About Our Commission Structure
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
            </div>
          </div>
        </section>

      {/* Section 4: Technology Features */}
        <section className="py-16 sm:py-24 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Technology That Works for You
              </h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                LessonLynx was built to support the way teachers and students actually work today — fast, flexible, and focused.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-10">
                <div className="bg-white p-6 rounded-lg shadow text-center">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Smart Match Engine</h3>
                    <p className="text-gray-700">Connects students with high-compatibility teachers based on skill, goals, and availability.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow text-center">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Protected Payments</h3>
                    <p className="text-gray-700">Automated billing with built-in policies for no-shows and cancellations.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow text-center">
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Streamlined Communication</h3>
                    <p className="text-gray-700">Everything happens within the app, so nothing gets lost or forgotten.</p>
                </div>
            </div>
             <p className="mt-10 text-center text-lg text-gray-700">
               Built for Growth: Future updates include progress tracking, scheduling insights, and more ways to help your teaching thrive.
            </p>
          </div>
        </section>

      {/* Section 5: Call to Action */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-blue-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-6">
              Let's Make Music Happen
            </h2>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Whether you're learning your first scale or building a full studio, LessonLynx gives you the tools to move forward with clarity and confidence.
            </p>
            <p className="text-xl text-gray-700 font-semibold mb-10">
              Your next great lesson is waiting.
            </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
                href="/instrument-selection" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition duration-300 ease-in-out text-lg transform hover:scale-105"
            >
                Join LessonLynx Today
            </a>
            <a 
              href="/for-teachers" 
              className="inline-block border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold py-3 px-8 rounded-lg shadow-md transition duration-300 ease-in-out text-lg"
            >
              Become a Teacher
            </a>
          </div>
          </div>
        </section>

      </div>
  );
};

export default AboutPage; 